import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { ConvexAuthProvider } from '@convex-dev/auth/react'
import { ConvexReactClient } from 'convex/react'
import { Toaster } from 'sonner'

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'
import { ThemeProvider } from './components/common/theme-provider.tsx'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthSyncProvider } from './features/auth'
import { initPerformanceMonitoring } from './lib/performance'
import { initSentry } from './lib/sentry'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

import './styles.css'
import { env } from '@/env.ts'

// Initialize Sentry (must be before other initializations)
initSentry()

// Initialize performance monitoring
initPerformanceMonitoring()

// Register service worker for caching (only in production)
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Service worker registration failed — app works without it
    })
  })
}

const convex = new ConvexReactClient(env.VITE_CONVEX_URL)

// Create a new router instance
const TanStackQueryProviderContext = TanStackQueryProvider.getContext()
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 30000, // Keep for route preloading
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <ConvexAuthProvider client={convex}>
        <ThemeProvider defaultTheme="system">
          <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
            <TooltipProvider>
              <AuthSyncProvider>
                <RouterProvider router={router} />
                <Toaster position="top-right" />
              </AuthSyncProvider>
            </TooltipProvider>
          </TanStackQueryProvider.Provider>
        </ThemeProvider>
      </ConvexAuthProvider>
    </StrictMode>,
  )
}
